import type { Meta } from '@numistic/contracts'
import type { RefResolver } from '@numistic/domain'
import { useRefNameMap, useTree } from '@numistic/data'
import { useMemo } from 'react'

export interface RefResolverOptions {
  /** Свои деревья (коллекции, места хранения) — только для владельца, не для ссылки «только чтение». */
  own?: boolean
  /** Разновидности записи каталога: id → название. */
  variants?: ReadonlyArray<{ id: string; label: string }>
}

/**
 * Разрешение id → название: справочники, которые нужны переданным полям, статусы из метаданных,
 * для владельца — его коллекции и места хранения, а также переданные разновидности.
 */
export function useRefResolver(
  meta: Meta | undefined,
  kinds: ReadonlySet<string>,
  options: RefResolverOptions = {},
): RefResolver {
  const names = useRefNameMap(kinds)
  const own = options.own ?? false
  const collections = useTree('collections', { enabled: own && kinds.has('collections') })
  const locations = useTree('locations', { enabled: own && kinds.has('storage_locations') })
  const { variants } = options
  return useMemo(() => {
    const statuses = new Map((meta?.statuses ?? []).map((s) => [s.id, s.name.ru]))
    const nodes = new Map(
      [...(collections.data ?? []), ...(locations.data ?? [])].map((n) => [n.id, n.name]),
    )
    const variantNames = new Map((variants ?? []).map((v) => [v.id, v.label]))
    return (kind: string, id: string) =>
      names.get(id) ??
      statuses.get(id) ??
      (kind === 'variants' ? variantNames.get(id) : nodes.get(id))
  }, [meta, names, collections.data, locations.data, variants])
}
